import { extends as _extends, toArray as _toArray } from '../../_virtual/_rollupPluginBabelHelpers.js';
import _mapValues from 'lodash/mapValues';
import { Alert } from '@zillow/constellation';
import { event } from '@zillow/universal-analytics';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useForm, FormProvider, useFormState } from 'react-hook-form';
import { getCmsInfoForPage } from '../../utils/clickstream/getCmsInfoForPage.js';
import { getTrackingParamsFromCurrentUrl } from '../../utils/utm.js';
import { usePageProps } from '../PagePropsProvider/PagePropsProvider.js';

var ApexForm = function ApexForm(_ref) {
  var businessLine = _ref.businessLine,
    children = _ref.children,
    defaultValues = _ref.defaultValues,
    endpoint = _ref.endpoint,
    id = _ref.id,
    warmLeadSource = _ref.warmLeadSource;
  var pageProps = usePageProps();
  var initialDefaultValues = Object.assign({}, defaultValues, {
    Business_Line: businessLine,
    Warm_Lead_Source: warmLeadSource,
    utm_medium: '',
    utm_term: '',
    utm_content: '',
    utm_source: '',
    utm_campaign: '',
    Internal_Tracking_Code: '',
    Test_Variation: '',
    URL: '',
    URL_Referrer: ''
  });
  var form = useForm({
    mode: 'onTouched',
    defaultValues: initialDefaultValues
  });

  // Set tracking value defaults.
  useEffect(function () {
    var currentTrackingValues = getTrackingParamsFromCurrentUrl();
    form.reset(Object.assign({}, initialDefaultValues, currentTrackingValues), {
      keepErrors: true,
      keepDirty: true,
      keepDirtyValues: true,
      keepDefaultValues: false,
      keepValues: false,
      keepIsSubmitted: true,
      keepTouched: true,
      keepIsValid: true,
      keepSubmitCount: true
    });
  },
  // Intentionally leaving out form.reset as this is a mount only effect
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  var submit = function submit(e) {
    var newLaneData = {
      // https://dataportal.zillowgroup.net/event-registry/2055
      envelope: {
        event_type_id: '2055',
        event_type_version_id: '1',
        event_template_id: '126',
        event_template_version_id: '1'
        // Master ID: 2055.1.126.1
      },
      clickstream_trigger: {
        trigger_type_nm: 'interaction',
        trigger_location_nm: 'cms',
        trigger_source_nm: 'cms_button',
        trigger_object_nm: 'no_trigger_object'
      },
      semantic: {
        semantic_event_nm: 'cms_submit'
      },
      cms_info: Object.assign({}, getCmsInfoForPage(pageProps), {
        block_id: id || '',
        block_type_cd: 'apex_form',
        form_business_line_txt: e.target.Business_Line.value,
        form_warm_lead_source_txt: e.target.Warm_Lead_Source.value,
        itc: e.target.Internal_Tracking_Code.value
        // These fields are added in based on success or failure or validation error.
        // // form_is_valid_ind: true | false,
        // // form_invalid_fields_txt: {},
        // // form_submit_success_ind: true | false,
        // // form_server_response_message_txt: '',

        // This field is crossed out on https://docs.google.com/spreadsheets/d/13sPb86043ojCqishXpIT30LLLFnK8C_pSyGoMbfNG9E/
        // so it's probably needed.
        // channel: ''
      })
    };
    form.handleSubmit(function (initialValues, e) {
      return new Promise(function ($return, $error) {
        var finalValues, _finalValues$Name$spl, _finalValues$Name$spl2, firstName, lastNameParts, lastName, form, hiddenInputs, res;
        finalValues = Object.assign({}, initialValues);
        if (finalValues.Name) {
          _finalValues$Name$spl = finalValues.Name.split(' '), _finalValues$Name$spl2 = _toArray(_finalValues$Name$spl), firstName = _finalValues$Name$spl2[0], lastNameParts = _finalValues$Name$spl2.slice(1);
          lastName = lastNameParts.join(' ');
          if (!finalValues.First_Name) {
            finalValues.First_Name = firstName;
          }
          if (!finalValues.Last_Name) {
            finalValues.Last_Name = lastName;
          }
        }

        // Get latest hidden field values. as setting hidden inputs
        // programttically with input.value does not trigger the
        // onChange.
        form = e.target;
        hiddenInputs = form.querySelectorAll('[type=hidden]');
        hiddenInputs.forEach(function (input) {
          finalValues[input.name] = input.value;
        });
        return Promise.resolve(fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: new URLSearchParams(Object.assign({}, finalValues, {
            tfa_dbFormId: 'WORDPRESS',
            Ok_to_contact: 1
          })).toString()
        })).then(function ($await_1) {
          try {
            res = $await_1;
            if (res.status !== 200) {
              return $error(new Error("Got failed status code: \"" + res.status + "\""));
            }

            // Only send event if the form is valid

            return Promise.resolve(res.text()).then(function ($await_2) {
              try {
                event(Object.assign({}, newLaneData, {
                  cms_info: Object.assign({}, newLaneData.cms_info, {
                    form_is_valid_ind: true,
                    form_invalid_fields_txt: [],
                    form_submit_success_ind: res.status === 200,
                    form_server_response_message_txt: $await_2
                  })
                }));
                return $return();
              } catch ($boundEx) {
                return $error($boundEx);
              }
            }, $error);
          } catch ($boundEx) {
            return $error($boundEx);
          }
        }, $error);
      });
    }, function (errors) {
      event(Object.assign({}, newLaneData, {
        cms_info: Object.assign({}, newLaneData.cms_info, {
          form_is_valid_ind: false,
          // Keys are field names
          form_invalid_fields_txt: _mapValues(errors, function (_ref2) {
            var message = _ref2.message,
              ref = _ref2.ref;
            return {
              error: message,
              value: ref.value
            };
          }),
          form_submit_success_ind: false,
          form_server_response_message_txt: ''
        })
      }));
    })(e).catch(function () {
      // Ignore the error but catch it. The form state will see this. But catch it
      // here to prevent "unhandled error" message.
    });
  };
  return /*#__PURE__*/React.createElement(FormProvider, form, /*#__PURE__*/React.createElement("form", {
    role: "form",
    onSubmit: submit,
    id: id,
    noValidate: true
  }, /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('Business_Line'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('Warm_Lead_Source'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('utm_medium'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('utm_term'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('utm_content'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('utm_source'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('utm_campaign'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('Internal_Tracking_Code'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('URL'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('URL_Referrer'))), /*#__PURE__*/React.createElement("input", _extends({
    type: "hidden"
  }, form.register('Test_Variation'))), children), /*#__PURE__*/React.createElement(SubmissionError, null));
};
function SubmissionError() {
  var formState = useFormState();
  var didSubmissionFail = formState.isSubmitted && !formState.isSubmitting && !formState.isSubmitSuccessful;
  return didSubmissionFail && /*#__PURE__*/React.createElement(Alert, {
    marginTop: "sm",
    appearance: "error",
    body: "An error occurred while processing your request. Review the information to ensure it's correct, then try again."
  });
}
ApexForm.propTypes = process.env.NODE_ENV !== "production" ? {
  defaultValues: PropTypes.shape({}),
  children: PropTypes.node.isRequired,
  businessLine: PropTypes.string.isRequired,
  warmLeadSource: PropTypes.string.isRequired,
  id: PropTypes.string
} : {};
ApexForm.defaultProps = {
  defaultValues: undefined,
  id: undefined
};
var ApexForm$1 = ApexForm;

export { ApexForm$1 as default };
